import { useState } from "react";

import { ApiError } from "@/api/client";
import { useContact } from "@/api/hooks";
import { useQuoteAction } from "@/api/sales";
import type { Quote } from "@/api/types";
import { Alert, Modal } from "@/components/ui";
import { DraftButton } from "@/features/ai/AiPanels";

const split = (s: string) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

export function SendQuoteDialog({ quote, onClose }: { quote: Quote; onClose: () => void }) {
  const contact = useContact(quote.contact?.id ?? "");
  const action = useQuoteAction();
  const [to, setTo] = useState<string | null>(null);
  const [cc, setCc] = useState("");
  const [message, setMessage] = useState("Thank you for your inquiry. Please find our quotation attached.");
  const [subject, setSubject] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toValue = to ?? contact.data?.email ?? "";

  const send = async () => {
    setError(null);
    try {
      await action.mutateAsync({ path: `/quotes/${quote.id}/send`, body: { to: split(toValue), cc: split(cc), message, ...(subject ? { subject } : {}) } });
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? (Object.values(e.fieldErrors())[0] ? `Check the email addresses: ${Object.values(e.fieldErrors())[0]}` : e.message) : "Could not send");
    }
  };

  return (
    <Modal title={`Send ${quote.number}${quote.version > 1 ? ` rev. ${quote.version}` : ""}`} onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); void send(); }}>
        {error && <Alert>{error}</Alert>}
        <div><label htmlFor="send-to" className="label">To</label><input id="send-to" className="input" value={toValue} onChange={(e) => setTo(e.target.value)} placeholder="buyer@customer.com" /></div>
        <div><label htmlFor="send-cc" className="label">Cc</label><input id="send-cc" className="input" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="Separate several with commas" /></div>
        {subject !== null && <div><label htmlFor="send-subject" className="label">Subject</label><input id="send-subject" className="input" value={subject} onChange={(e) => setSubject(e.target.value)} /></div>}
        <div className="flex justify-end"><DraftButton label="Write it with AI" body={{ kind: "quote_email", quote_id: quote.id }} onDraft={(d) => { setMessage(d.body); if (d.subject) setSubject(d.subject); }} /></div>
        <div><label htmlFor="send-message" className="label">Message</label><textarea id="send-message" className="input min-h-24" value={message} onChange={(e) => setMessage(e.target.value)} /></div>
        <p className="text-xs text-muted">The PDF is attached, and the email includes a link where your customer can view, accept or decline the quote online.</p>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={!toValue.trim() || action.isPending}>{action.isPending ? "Sending…" : "Send quote"}</button>
        </div>
      </form>
    </Modal>
  );
}
